/**
 * THE one way this console talks to the backend. Nothing else calls fetch() on the API.
 *
 * Built from the two consoles that already run against tkt-backend:
 *  - tkt-back-office lib/backOfficeApi.js — cookies on every request, cross-org scope as the
 *    `?orgScope=all` query param on reads (keeps GETs CORS-simple, no preflight) and the
 *    `x-org-scope: all` header on writes, the operator's reason as `x-reason`, the reader's time
 *    zone on report reads, identical in-flight GETs joined, a hard timeout, the date contract on
 *    every write body (lib/absoluteInstant).
 *  - mtn-support lib/api.ts — one error reader for every error shape the backend answers with
 *    (`{error}`, `{errors:[…]}`, `{message}`, better-auth `{code,message}`), so the operator sees
 *    what the server actually said.
 *
 * Reads THROW on failure (an ApiError), which is what React Query wants: a failed read is an
 * error state, never an empty list.
 */
import { getBackendUrl } from './backendUrl.js';
import { withAbsoluteInstants } from './absoluteInstant.js';

export const API_BASE = getBackendUrl();

export class ApiError extends Error {
  /**
   * @param {string} message  what the server said, or a plain description of what failed
   * @param {{ status?: number, body?: unknown, kind?: string }} [info]
   */
  constructor(message, { status = 0, body = null, kind } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.kind = kind || kindOf(status);
  }
}

const kindOf = (status) => {
  if (!status) return 'network';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 400 || status === 422) return 'validation';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'other';
};

/** The most specific message the server gave, whichever shape the route chose. */
export const messageFrom = (body, status, statusText = '') => {
  const first = Array.isArray(body?.errors) ? body.errors[0] : null;
  const msg =
    (typeof first === 'string' ? first : first?.message) ||
    (typeof body?.error === 'string' ? body.error : body?.error?.message) ||
    body?.message ||
    (typeof body === 'string' && body.trim() && body.length < 300 ? body.trim() : null);
  return String(msg || `Request failed (${status}${statusText ? ` ${statusText}` : ''})`);
};

const READER_TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } })();

/** Build the URL for a read: query params, cross-org scope, and the reader's clock on reports. */
export const buildUrl = (path, query) => {
  const url = new URL(path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) { if (v.length) url.searchParams.set(k, v.join(',')); continue; }
      url.searchParams.set(k, String(v));
    }
  }
  if (!url.searchParams.has('orgScope')) url.searchParams.set('orgScope', 'all');
  if (url.pathname.startsWith('/api/reports') && !url.searchParams.has('tz')) url.searchParams.set('tz', READER_TZ);
  return url.toString();
};

const parseBody = async (res) => {
  const type = res.headers.get('content-type') || '';
  if (res.status === 204) return null;
  if (type.includes('application/json')) return res.json().catch(() => null);
  return res.text().catch(() => '');
};

const withTimeout = (signal, ms) => {
  const controller = new AbortController();
  let timedOut = false;
  const onAbort = () => controller.abort();
  if (signal && typeof signal.addEventListener === 'function') {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onAbort, { once: true });
  }
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, ms);
  return {
    signal: controller.signal,
    didTimeOut: () => timedOut,
    cleanup: () => { clearTimeout(timer); signal?.removeEventListener?.('abort', onAbort); },
  };
};

async function rawRequest(url, { method = 'GET', headers = {}, body, signal, timeoutMs = 20000 } = {}) {
  const t = withTimeout(signal, timeoutMs);
  let res;
  try {
    res = await fetch(url, { method, headers: { Accept: 'application/json', ...headers }, body, signal: t.signal, credentials: 'include' });
  } catch (err) {
    if (t.didTimeOut()) throw new ApiError(`No response after ${Math.round(timeoutMs / 1000)}s`, { kind: 'timeout' });
    if (err?.name === 'AbortError') throw err;
    throw new ApiError('Could not reach the server. Check your connection and try again.', { kind: 'network' });
  } finally {
    t.cleanup();
  }
  const payload = await parseBody(res);
  if (!res.ok) throw new ApiError(messageFrom(payload, res.status, res.statusText), { status: res.status, body: payload });
  return payload;
}

/**
 * THE SAME QUESTION, ASKED ONCE (tkt-back-office fetchBackOfficeJson). While a GET for a URL is in
 * flight, a second request for that URL joins it. Not a cache: the entry is dropped the moment the
 * request settles, and the shared request is only aborted once every joiner has walked away.
 */
const inFlight = new Map();

/** GET JSON. Throws ApiError on any failure. */
export async function getJson(path, { query, signal, timeoutMs = 20000 } = {}) {
  const url = buildUrl(path, query);
  const key = `${timeoutMs}|${url}`;
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  let shared = inFlight.get(key);
  if (!shared) {
    const controller = new AbortController();
    shared = { controller, joiners: 0 };
    shared.promise = rawRequest(url, { signal: controller.signal, timeoutMs });
    const forget = () => { if (inFlight.get(key) === shared) inFlight.delete(key); };
    shared.promise.then(forget, forget);
    inFlight.set(key, shared);
  }
  shared.joiners += 1;
  let left = false;
  const leave = () => {
    if (left) return;
    left = true;
    shared.joiners -= 1;
    if (shared.joiners > 0) return;
    if (inFlight.get(key) === shared) inFlight.delete(key);
    shared.controller.abort();
  };
  if (!signal) {
    try { return await shared.promise; } finally { leave(); }
  }
  try {
    return await new Promise((resolve, reject) => {
      const onAbort = () => reject(new DOMException('Aborted', 'AbortError'));
      signal.addEventListener('abort', onAbort, { once: true });
      shared.promise.then(
        (v) => { signal.removeEventListener('abort', onAbort); resolve(v); },
        (e) => { signal.removeEventListener('abort', onAbort); reject(e); },
      );
    });
  } finally { leave(); }
}

/** The operator's reason for an act, recorded by the server on that act (tkt-backend auditActor). */
const reasonHeader = (reason) => (reason && String(reason).trim() ? { 'x-reason': encodeURIComponent(String(reason).trim()) } : {});

/**
 * POST / PATCH / PUT / DELETE JSON. Throws ApiError on failure.
 *   sendJson('/api/payouts/approve', { body: { payoutId }, reason: 'Checked statement' })
 * Date fields are turned into local-midnight instants (lib/absoluteInstant); `plainDates: true`
 * sends them as written, for the routes that take a wall-clock day (a hold's endDate).
 */
export async function sendJson(path, { method = 'POST', body, query, reason, signal, timeoutMs = 30000, plainDates = false } = {}) {
  const url = new URL(path.startsWith('http') ? path : `${API_BASE}${path}`);
  if (query) for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  if (url.pathname.startsWith('/api/reports')) url.searchParams.set('tz', READER_TZ);
  return rawRequest(url.toString(), {
    method,
    signal,
    timeoutMs,
    headers: { 'x-org-scope': 'all', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...reasonHeader(reason) },
    body: body !== undefined ? JSON.stringify(plainDates ? body : withAbsoluteInstants(body)) : undefined,
  });
}

/**
 * The rows of a list response, whichever envelope the route uses:
 * `{data:[…]}`, `{rows:[…]}`, a bare array, or one of the named keys some routes use.
 */
export const listOf = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  for (const key of ['data', 'rows', 'items', 'orders', 'events', 'users', 'logs', 'organizations', 'payouts', 'grants']) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  if (payload.data && typeof payload.data === 'object') return listOf(payload.data);
  return [];
};

/**
 * One page of a paged list, normalised. The backend pages by offset and answers `hasMore` (it
 * fetches one extra row); exact totals only exist when asked for with `includeTotal=true`, and
 * then under different names per route (`totalItems`, `total`). Unknown total stays null — the
 * UI says "Page 2" rather than inventing "of 0".
 * @returns {{ rows: any[], hasMore: boolean, total: number|null, page: number }}
 */
export const pageOf = (payload, requestedPage = 1) => {
  const rows = listOf(payload);
  const total = payload?.totalItems ?? payload?.total ?? payload?.count ?? null;
  const page = Number(payload?.currentPage ?? payload?.page ?? requestedPage) || requestedPage;
  const hasMore = typeof payload?.hasMore === 'boolean' ? payload.hasMore : total != null ? page * rows.length < total : false;
  return { rows, hasMore, total: total == null ? null : Number(total), page };
};

/**
 * Every row of a SMALL reference list (organizations for a picker, venues), following `hasMore`
 * with a hard page ceiling so a backend that always answers hasMore cannot spin here.
 */
export async function fetchAllPages(path, { query = {}, limit = 100, maxPages = 20, signal } = {}) {
  const rows = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const payload = await getJson(path, { query: { ...query, page, limit }, signal });
    const batch = listOf(payload);
    rows.push(...batch);
    if (!payload?.hasMore || batch.length === 0) return rows;
  }
  if (import.meta.env.DEV) console.warn(`[api] stopped at ${maxPages} pages for ${path}; ${rows.length} rows`);
  return rows;
}

/** Download a file the backend renders (statements, exports). With no `filename`, the server's own. */
export async function download(path, filename, { query } = {}) {
  const res = await fetch(buildUrl(path, query), { credentials: 'include' });
  if (!res.ok) throw new ApiError(messageFrom(await parseBody(res), res.status), { status: res.status });
  const named = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(res.headers.get('content-disposition') || '');
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename || (named ? decodeURIComponent(named[1]) : 'download');
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
}

/**
 * POST a question that answers with a file (the report engine's exports) and save it. The file's
 * name comes from the server's Content-Disposition when it sends one. Returns the export's
 * headers the caller may show (rows written, whether it was cut short).
 */
export async function downloadPost(path, body, fallbackName) {
  const url = new URL(path.startsWith('http') ? path : `${API_BASE}${path}`);
  url.searchParams.set('orgScope', 'all');
  if (url.pathname.startsWith('/api/reports')) url.searchParams.set('tz', READER_TZ);
  const res = await fetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: { 'x-org-scope': 'all', 'Content-Type': 'application/json' },
    body: JSON.stringify(withAbsoluteInstants(body)),
  });
  if (!res.ok) throw new ApiError(messageFrom(await parseBody(res), res.status), { status: res.status });
  const named = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(res.headers.get('content-disposition') || '');
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = href;
    a.download = named ? decodeURIComponent(named[1]) : fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
  const rows = res.headers.get('x-export-rows');
  // The report engine's file route says a cut-short file with X-Truncated; the old exports with X-Export-Truncated.
  return {
    rows: rows != null ? Number(rows) : null,
    truncated: res.headers.get('x-export-truncated') === 'true' || res.headers.get('x-truncated') === 'true',
    exportId: res.headers.get('x-export-id'),
  };
}
